import { useEffect, useRef, useState } from "react";
import { Buton, Iskelet, Kart } from "@koclukpanel/ui";
import { ApiHatasi } from "./api";
import { davetDurumu, davetKoduAl } from "./api-oyun2";
import { hataMetni } from "./hata-metni";
import type { DavetDurumu } from "@koclukpanel/contracts";

/**
 * DAVET — F-GAM-18, oyunlaştırma dalga 2 / sürüm 7b (öğrenci tarafı).
 *
 * Ekran YALNIZ kodu gösterir ve kopyalatır. Arkadaşın adı/bilgisi hiçbir yerde yok, sistem mesaj göndermez
 * (G-COC-06) ve "paylaş / mesaj gönder" bağlantısı (WhatsApp, SMS) bilerek YOK: öğrenci kodu kendi söyler.
 *
 * Miktarlar (davet eden / edilen puan, bekleme günü) SUNUCUDAN, kural sürümünden gelir; ekranda sabit sayı yok.
 * Kural tanımlı değilse sunucu `null` döner ve ekran HİÇ miktar yazmaz — "0 puan" ya da tahmini bir sayı yazmak
 * olmayan bir söz vermek olurdu. Hangi arkadaşın puanı yazıldı, ne zaman yazılacak gibi kararlar sunucuda;
 * ekran yalnız sayıları gösterir, hesaplamaz.
 */

/** Okunaklı görünüm için 4'lü gruplar; kopyalanan değer gruplar BİTİŞİK olduğundan boşluksuz kalır. */
function gruplaBol(kod: string): string[] {
  const gruplar: string[] = [];
  for (let i = 0; i < kod.length; i += 4) gruplar.push(kod.slice(i, i + 4));
  return gruplar;
}

export function Davet() {
  const [durum, setDurum] = useState<DavetDurumu | null>(null);
  const [kapali, setKapali] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [yenile, setYenile] = useState(0);
  const [olusturuluyor, setOlusturuluyor] = useState(false);
  const [islemHatasi, setIslemHatasi] = useState<string | null>(null);
  const [kopyaMesaji, setKopyaMesaji] = useState("");
  const kodRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let iptal = false;
    void (async () => {
      try {
        const d = await davetDurumu();
        if (!iptal) {
          setHata(null);
          setDurum(d);
        }
      } catch (e) {
        if (iptal) return;
        if (e instanceof ApiHatasi && e.durum === 404) setKapali(true);
        else setHata(hataMetni(e, "Davet bilgisi alınamadı."));
      }
    })();
    return () => {
      iptal = true;
    };
  }, [yenile]);

  async function kodOlustur() {
    setOlusturuluyor(true);
    setIslemHatasi(null);
    try {
      setDurum(await davetKoduAl());
    } catch (e) {
      if (e instanceof ApiHatasi && e.durum === 404) setKapali(true);
      else setIslemHatasi(hataMetni(e, "Davet kodu oluşturulamadı."));
    } finally {
      setOlusturuluyor(false);
    }
  }

  /**
   * Kopyalama: panoya yazma yalnız güvenli bağlamda (https) ve izinle çalışır. Çalışmazsa kod metni SEÇİLİR ve
   * kullanıcıya "basılı tut" denir; her iki durumda da sonuç `role="status"` ile duyurulur, sessiz başarısızlık yok.
   */
  async function kopyala(kod: string) {
    try {
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.clipboard?.writeText === "function"
      ) {
        await navigator.clipboard.writeText(kod);
        setKopyaMesaji("Kod kopyalandı.");
        return;
      }
    } catch {
      // Pano reddedildi: aşağıdaki seçim yedeğine düş.
    }
    const dugum = kodRef.current;
    if (dugum && typeof window.getSelection === "function") {
      const secim = window.getSelection();
      const aralik = document.createRange();
      aralik.selectNodeContents(dugum);
      secim?.removeAllRanges();
      secim?.addRange(aralik);
    }
    setKopyaMesaji("Kod seçildi. Kopyalamak için basılı tutup Kopyala'yı seç.");
  }

  if (kapali) return <p className="text-notr-600 text-sm">Davet şu an kapalı.</p>;
  if (hata && durum === null) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="rounded-alan bg-hata-50 text-hata-700 w-full px-3 py-2 text-sm">
          {hata}
        </p>
        <Buton
          gorunum="ikincil"
          onClick={() => {
            setHata(null);
            setYenile((n) => n + 1);
          }}
        >
          Tekrar dene
        </Buton>
      </div>
    );
  }
  if (durum === null) return <Iskelet satir={4} />;

  const miktarVar = durum.davetEdenPuan !== null && durum.davetEdilenPuan !== null;

  return (
    <div className="flex flex-col gap-3">
      <Kart baslikSeviyesi={3} baslik="Davet kodun">
        <p className="text-notr-700 text-sm">
          Arkadaşın kayıt olurken bu kodu söylerse ve ilk ödemesini yaparsa ikiniz de puan
          kazanırsınız.
        </p>
        {miktarVar && (
          <p className="text-notr-900 mt-1 text-sm font-medium">
            Sen {durum.davetEdenPuan} puan, arkadaşın {durum.davetEdilenPuan} puan kazanır.
          </p>
        )}

        {durum.kod === null ? (
          <div className="mt-3 flex flex-col items-start gap-2">
            <Buton onClick={() => void kodOlustur()} disabled={olusturuluyor}>
              {olusturuluyor ? "Oluşturuluyor…" : "Davet kodunu oluştur"}
            </Buton>
            {islemHatasi && (
              <p
                role="alert"
                className="rounded-alan bg-hata-50 text-hata-700 w-full px-3 py-2 text-sm"
              >
                {islemHatasi}
              </p>
            )}
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            <p className="text-notr-900 text-3xl leading-tight font-semibold">
              <span
                ref={kodRef}
                role="img"
                aria-label={`Davet kodun: ${durum.kod.split("").join(", ")}`}
                className="font-mono tracking-widest select-all"
                data-testid="davet-kodu"
              >
                {gruplaBol(durum.kod).map((g, i) => (
                  <span key={i} className={i > 0 ? "ml-3" : undefined}>
                    {g}
                  </span>
                ))}
              </span>
            </p>
            <div>
              <Buton gorunum="ikincil" onClick={() => void kopyala(durum.kod!)}>
                Kopyala
              </Buton>
            </div>
          </div>
        )}
        {/* Her zaman DOM'da: sonradan eklenen bir canlı bölge bazı ekran okuyucularda duyurulmaz. */}
        <p role="status" className="text-notr-600 mt-2 min-h-5 text-sm">
          {kopyaMesaji}
        </p>
      </Kart>

      {durum.kod !== null && (
        <Kart baslikSeviyesi={3} baslik="Davetlerin">
          <ul className="flex flex-col gap-3">
            <li className="flex items-baseline justify-between gap-3">
              <span className="text-notr-700 text-sm">İlk ödemesini yapan arkadaş</span>
              <span className="text-notr-900 text-xl font-semibold tabular-nums">
                {durum.ilkOdemeYapan}
              </span>
            </li>
            <li>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-notr-700 text-sm">Puanı beklemede</span>
                <span className="text-notr-900 text-xl font-semibold tabular-nums">
                  {durum.beklemede}
                </span>
              </div>
              <p className="text-notr-600 mt-0.5 text-xs">
                {durum.beklemeGun !== null
                  ? `Ödemeden ${durum.beklemeGun} gün sonra puanın yazılır.`
                  : "Bekleme süresi dolunca puanın yazılır."}
              </p>
            </li>
            <li className="flex items-baseline justify-between gap-3">
              <span className="text-notr-700 text-sm">Puanı yazılan</span>
              <span className="text-notr-900 text-xl font-semibold tabular-nums">
                {durum.puanYazilan}
              </span>
            </li>
          </ul>
        </Kart>
      )}
    </div>
  );
}
